import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../../components/layout/Layout";
import { dataExchangeApi, saveFile } from "../../../api/adminToolsApi";
import {
  useCreateDataTemplate,
  useDataTemplates,
  useDatasets,
  useDeleteDataTemplate,
  useRunDataImport,
} from "../../../api/queries/adminToolsQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import { MODE_HELP, MODE_LABEL, TemplatePicks } from "./DataToolBits";
import ImportTabs from "./ImportTabs";
import { SaveTemplateDialog } from "./ExportPage";
import "../admin.css";
import { useCanModify } from "../../../hooks/usePermissions";

const EMPTY = [];
const MAX_ROWS = 5000;
const STEPS = ["Choose", "Upload & check", "Match columns", "Review", "Done"];
const ACTION_TONE = {
  CREATE: "green",
  UPDATE: "blue",
  SKIP: "gray",
  ERROR: "red",
};
const ACTION_LABEL = {
  CREATE: "Will add",
  UPDATE: "Will update",
  SKIP: "Skipped",
  ERROR: "Problem",
};

const errorText = async (err, fallback) => {
  let msg = getApiErrorMessage(err, fallback);
  try {
    const text = await err?.response?.data?.text?.();
    if (text) msg = JSON.parse(text).message || msg;
  } catch {
    /* not a blob */
  }
  return Array.isArray(msg) ? msg.join(" ") : msg;
};

const Summary = ({ summary = {} }) => (
  <div className="dt-summary">
    <div className="dt-stat">
      <span className="approval-meta-label">Rows</span>
      <strong>{summary.total ?? 0}</strong>
    </div>
    <div className="dt-stat dt-stat--create">
      <span className="approval-meta-label">Create</span>
      <strong>{summary.create ?? 0}</strong>
    </div>
    <div className="dt-stat dt-stat--update">
      <span className="approval-meta-label">Update</span>
      <strong>{summary.update ?? 0}</strong>
    </div>
    <div className="dt-stat">
      <span className="approval-meta-label">Skip</span>
      <strong>{summary.skip ?? 0}</strong>
    </div>
    <div className="dt-stat dt-stat--failed">
      <span className="approval-meta-label">Failed</span>
      <strong>{summary.failed ?? 0}</strong>
    </div>
  </div>
);

/**
 * Import wizard: dataset + mode → upload (dry run) → map columns (re-run the
 * dry run) → preview → import → download the failed rows to fix and re-upload.
 * Max 5,000 rows per file. Mappings can be saved as templates.
 */
const ImportPage = () => {
  // Dry runs are read-only; the real import needs Data tools: modify
  const canModify = useCanModify("data-tools", { company: true });
  const datasetsQuery = useDatasets();
  const datasets = (datasetsQuery.data || EMPTY).filter((d) => d.canImport);
  const [datasetKey, setDatasetKey] = useState("");
  const dataset = datasets.find((d) => d.key === datasetKey) || null;
  const [mode, setMode] = useState("");
  const [templateId, setTemplateId] = useState(null);
  const [file, setFile] = useState(null);
  const [mapping, setMapping] = useState({}); // { "File header": fieldKey | null }
  const [result, setResult] = useState(null); // last dry run
  const [done, setDone] = useState(null); // real import result
  const [step, setStep] = useState(0);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const fileRef = useRef(null);

  const run = useRunDataImport();
  const templates = useDataTemplates(
    { dataset: datasetKey, kind: "IMPORT" },
    { enabled: !!datasetKey },
  );
  const createTemplate = useCreateDataTemplate();
  const deleteTemplate = useDeleteDataTemplate();

  useEffect(() => {
    if (!datasetKey && datasets.length) setDatasetKey(datasets[0].key);
  }, [datasetKey, datasets]);
  useEffect(() => {
    setMode(dataset?.importModes?.[0] || "");
    setTemplateId(null);
    resetFile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset]);

  const importable = useMemo(
    () => (dataset?.fields || []).filter((f) => f.importable !== false),
    [dataset],
  );
  const required = importable.filter(
    (f) => f.required || (mode !== "UPDATE" && f.requiredOnCreate),
  );
  const mappedFields = new Set(Object.values(mapping).filter(Boolean));
  const missingRequired = required.filter((f) => !mappedFields.has(f.key));

  function resetFile() {
    setFile(null);
    setResult(null);
    setDone(null);
    setMapping({});
    setStep(0);
    if (fileRef.current) fileRef.current.value = "";
  }

  const dryRun = (useMapping) =>
    run.mutate(
      {
        file,
        dataset: datasetKey,
        mode,
        dryRun: true,
        templateId: templateId || undefined,
        mapping: useMapping ? mapping : undefined,
      },
      {
        onSuccess: (res) => {
          setResult(res);
          const next = {};
          (res.mapping || []).forEach(
            (m) => (next[m.header] = m.field || null),
          );
          (res.ignoredHeaders || []).forEach((h) => (next[h] = null));
          setMapping(next);
          setErrorsOnly((res.summary?.failed || 0) > 0);
          setStep(useMapping ? 3 : 2);
        },
        onError: async (err) =>
          toast.error(await errorText(err, "Could not read the file.")),
      },
    );

  const doImport = () =>
    run.mutate(
      {
        file,
        dataset: datasetKey,
        mode,
        dryRun: false,
        templateId: templateId || undefined,
        mapping,
      },
      {
        onSuccess: (res) => {
          setDone(res);
          setStep(4);
          toast.success(
            `Imported: ${res.summary?.create ?? 0} created, ${res.summary?.update ?? 0} updated, ${res.summary?.failed ?? 0} failed.`,
          );
        },
        onError: async (err) =>
          toast.error(await errorText(err, "Could not import.")),
      },
    );

  const download = async (fn, fallback) => {
    try {
      saveFile(await fn());
    } catch (err) {
      toast.error(await errorText(err, fallback));
    }
  };

  const rows = (result?.rows || EMPTY).filter(
    (r) => !errorsOnly || r.action === "ERROR",
  );
  const ignoredCount = Object.values(mapping).filter((f) => !f).length;
  // Nothing to decide on the columns: keep the table folded away
  const columnsOk =
    !!result &&
    missingRequired.length === 0 &&
    ignoredCount === 0 &&
    !(result.warnings || []).length;
  const importCount =
    (result?.summary?.create || 0) + (result?.summary?.update || 0);
  const failedCount = result?.summary?.failed || 0;
  // matchKey is { field, help } (a plain string on older servers)
  const mk = dataset?.matchKey;
  const matchKeyText =
    typeof mk === "string"
      ? mk
      : mk?.help ||
        importable.find((f) => f.key === mk?.field)?.label ||
        mk?.field;

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="mb-4">
          <Link
            to="/admin"
            className="text-slate-500 fs-080 text-decoration-none"
          >
            <i className="bi bi-arrow-left"></i> Administration
          </Link>
          <h1 className="fw-bold text-dark m-0 page-title">Import</h1>
          <p className="text-muted small mb-0">
            Bring in one kind of data from Excel or CSV (up to{" "}
            {MAX_ROWS.toLocaleString()} rows). You check everything before
            anything is saved.
          </p>
        </div>

        <ImportTabs />
        <p className="fs-080 text-slate-600 mb-3">
          <i className="bi bi-lightbulb me-1"></i>
          Adding new employees with all their details (personal, job, family…)?
          Use <Link to="/employee/import">New employees (workbook)</Link>{" "}
          instead.
        </p>

        <ol className="dt-steps" aria-label="Steps">
          {STEPS.map((label, i) => (
            <li
              key={label}
              className={i === step ? "is-active" : i < step ? "is-done" : ""}
            >
              {i < step ? (
                <i className="bi bi-check2"></i>
              ) : (
                <span>{i + 1}</span>
              )}{" "}
              {label}
            </li>
          ))}
        </ol>

        {datasetsQuery.isError && (
          <div className="alert alert-danger fs-085">
            {getApiErrorMessage(
              datasetsQuery.error,
              "Could not load datasets.",
            )}
          </div>
        )}

        {/* 1. Set up + 2. Upload */}
        <section className="admin-card mb-3">
          <div className="row g-3">
            <div className="col-lg-5">
              <h2 className="admin-card-title">
                <span className="dt-num">1</span>What are you importing?
              </h2>
              <select
                className="form-select mb-2"
                value={datasetKey}
                onChange={(e) => setDatasetKey(e.target.value)}
                aria-label="Dataset"
                disabled={run.isPending}
              >
                {datasets.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.label}
                  </option>
                ))}
              </select>
              {dataset?.description && (
                <p className="fs-080 text-slate-600">{dataset.description}</p>
              )}
              <h2 className="admin-card-title mt-3">
                <span className="dt-num">2</span>What should the import do?
              </h2>
              <div
                className="d-flex flex-column gap-2"
                role="radiogroup"
                aria-label="Mode"
              >
                {(dataset?.importModes || EMPTY).map((m) => (
                  <label
                    key={m}
                    className={`dt-mode ${mode === m ? "active" : ""}`}
                  >
                    <input
                      type="radio"
                      className="form-check-input mt-1"
                      name="import-mode"
                      checked={mode === m}
                      onChange={() => {
                        setMode(m);
                        setResult(null);
                        setStep(file ? 1 : 0);
                      }}
                    />
                    <span>
                      <strong>{MODE_LABEL[m] || m}</strong>
                      <span className="d-block fs-075 text-slate-500">
                        {MODE_HELP[m] || ""}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              {dataset?.matchKey && (
                <p className="fs-075 text-slate-500 mt-2 mb-0">
                  <i className="bi bi-key me-1"></i>Existing records are found
                  by <strong>{matchKeyText}</strong>.
                </p>
              )}
            </div>
            <div className="col-lg-7">
              <h2 className="admin-card-title">
                <span className="dt-num">3</span>Get the template
              </h2>
              <p className="fs-080 text-slate-600 mb-2">
                Download it, fill in one row per record, and keep the header row
                as it is.
              </p>
              <TemplatePicks
                label="Saved column setups:"
                templates={templates.data || EMPTY}
                activeId={templateId}
                onPick={(t) => {
                  setTemplateId(t.id === templateId ? null : t.id);
                  if (t.mode) setMode(t.mode);
                }}
                onDelete={(t) =>
                  window.confirm(`Delete template "${t.name}"?`) &&
                  deleteTemplate.mutate(t.id, {
                    onSuccess: () => toast.success("Template deleted."),
                  })
                }
              />
              <div className="d-flex flex-wrap gap-2 my-3">
                <button
                  type="button"
                  className="btn btn-sm btn-light border"
                  onClick={() =>
                    download(
                      () =>
                        dataExchangeApi.importTemplate({
                          dataset: datasetKey,
                          format: "xlsx",
                          templateId,
                        }),
                      "Could not download the template.",
                    )
                  }
                  disabled={!datasetKey}
                >
                  <i className="bi bi-file-earmark-excel me-1"></i>Excel
                  template
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-light border"
                  onClick={() =>
                    download(
                      () =>
                        dataExchangeApi.importTemplate({
                          dataset: datasetKey,
                          format: "csv",
                          templateId,
                        }),
                      "Could not download the template.",
                    )
                  }
                  disabled={!datasetKey}
                >
                  CSV template
                </button>
              </div>
              {required.length > 0 && (
                <p className="fs-075 text-slate-500 mb-3">
                  Must be filled in: {required.map((f) => f.label).join(", ")}.
                </p>
              )}
              <h2 className="admin-card-title">
                <span className="dt-num">4</span>Upload your file
              </h2>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <input
                  ref={fileRef}
                  type="file"
                  className="form-control bio-file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => {
                    setFile(e.target.files?.[0] || null);
                    setResult(null);
                    setDone(null);
                    setStep(e.target.files?.[0] ? 1 : 0);
                  }}
                  aria-label="File to import"
                />
                <button
                  type="button"
                  className="btn btn-primary profile-btn"
                  onClick={() => dryRun(false)}
                  disabled={!file || !mode || run.isPending}
                >
                  {run.isPending && run.variables?.dryRun && !result && (
                    <span
                      className="spinner-border spinner-border-sm"
                      aria-hidden="true"
                    ></span>
                  )}
                  <i className="bi bi-search"></i> Check file
                </button>
              </div>
              <p className="fs-075 text-slate-500 mt-2 mb-0">
                Checking saves nothing. You review the result first.
              </p>
            </div>
          </div>
        </section>

        {/* 3. Mapping */}
        {result && !done && (
          <section className="admin-card mb-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <h2 className="admin-card-title m-0">
                <span className="dt-num">5</span>Match your columns
              </h2>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setSaving(true)}
                >
                  <i className="bi bi-bookmark-plus me-1"></i>Save as a column
                  setup
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => dryRun(true)}
                  disabled={run.isPending}
                >
                  {run.isPending && run.variables?.dryRun && (
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      aria-hidden="true"
                    ></span>
                  )}
                  Check again
                </button>
              </div>
            </div>
            {(result.warnings || []).length > 0 && (
              <div className="alert alert-warning fs-080 py-2">
                <ul className="mb-0 ps-3">
                  {result.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
            {missingRequired.length > 0 && (
              <div className="alert alert-danger fs-080 py-2">
                These must be filled in but no column is matched to them yet:{" "}
                {missingRequired.map((f) => f.label).join(", ")}. Pick the
                column for each below, then press Check again.
              </div>
            )}
            {columnsOk && (
              <p className="fs-080 text-success mb-2">
                <i className="bi bi-check-circle-fill me-1"></i>All{" "}
                {Object.keys(mapping).length} columns matched.{" "}
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 align-baseline"
                  onClick={() => setShowColumns((v) => !v)}
                >
                  {showColumns ? "Hide columns" : "Show columns"}
                </button>
              </p>
            )}
            {!columnsOk && ignoredCount > 0 && (
              <p className="fs-080 text-slate-600 mb-2">
                {ignoredCount} column(s) in your file aren&apos;t matched and
                will be ignored. Change that below if they should be imported.
              </p>
            )}
            {(!columnsOk || showColumns) && (
              <div className="table-responsive">
                <table className="table table-sm mb-0 dt-mapping">
                  <thead>
                    <tr>
                      <th>Column in your file</th>
                      <th>Imports into</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(mapping).map((header) => (
                      <tr key={header}>
                        <td className="fw-semibold">{header}</td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={mapping[header] || ""}
                            onChange={(e) =>
                              setMapping((m) => ({
                                ...m,
                                [header]: e.target.value || null,
                              }))
                            }
                            aria-label={`Field for ${header}`}
                          >
                            <option value="">Ignore this column</option>
                            {importable.map((f) => (
                              <option
                                key={f.key}
                                value={f.key}
                                disabled={
                                  mappedFields.has(f.key) &&
                                  mapping[header] !== f.key
                                }
                              >
                                {f.label}
                                {f.required ||
                                (mode !== "UPDATE" && f.requiredOnCreate)
                                  ? " *"
                                  : ""}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* 4. Preview */}
        {result && !done && (
          <section className="admin-card mb-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <h2 className="admin-card-title m-0">
                <span className="dt-num">6</span>Review (nothing saved yet)
              </h2>
              <div className="form-check form-switch m-0">
                <input
                  id="dt-errors-only"
                  type="checkbox"
                  className="form-check-input"
                  checked={errorsOnly}
                  onChange={(e) => setErrorsOnly(e.target.checked)}
                />
                <label
                  htmlFor="dt-errors-only"
                  className="form-check-label fs-080"
                >
                  Problems only
                </label>
              </div>
            </div>
            <Summary summary={result.summary} />
            <div className="dt-preview">
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Action</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.row}>
                      <td className="tabular-nums">{r.row}</td>
                      <td>
                        <span
                          className={`status-pill status-pill--${ACTION_TONE[r.action] || "gray"}`}
                        >
                          {ACTION_LABEL[r.action] || r.action}
                        </span>
                      </td>
                      <td>
                        {r.message}
                        {(r.errors || []).length > 0 && (
                          <ul className="mb-0 ps-3 text-danger">
                            {r.errors.map((e, i) => (
                              <li key={i}>
                                {typeof e === "string"
                                  ? e
                                  : e.message || JSON.stringify(e)}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-slate-500">
                        {errorsOnly ? "No problems." : "No rows."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {result.rowsTruncated && (
              <p className="fs-070 text-slate-500 mt-1 mb-0">
                Only the first rows are shown; the summary counts every row.
              </p>
            )}
            <div className="d-flex flex-wrap justify-content-end align-items-center gap-3 mt-3">
              {failedCount > 0 && importCount > 0 && (
                <span className="fs-080 text-slate-600">
                  {failedCount} row(s) with problems will be left out. After the
                  import you can download them, fix them and upload that file
                  again.
                </span>
              )}
              {!canModify && (
                <span className="fs-080 text-slate-600">
                  You can check files but not import them.
                </span>
              )}
              <button
                type="button"
                className="btn btn-success profile-btn"
                onClick={doImport}
                disabled={
                  !canModify ||
                  run.isPending ||
                  missingRequired.length > 0 ||
                  !importCount
                }
              >
                {run.isPending && !run.variables?.dryRun && (
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  ></span>
                )}
                <i className="bi bi-check2-circle"></i> Import {importCount}{" "}
                row(s)
              </button>
            </div>
          </section>
        )}

        {/* 5. Done */}
        {done && (
          <section className="admin-card mb-3">
            <h2 className="admin-card-title">
              <i className="bi bi-check-circle text-success"></i>Imported
            </h2>
            <Summary summary={done.summary} />
            <div className="d-flex flex-wrap gap-2">
              {done.logId && (done.summary?.failed || 0) > 0 && (
                <button
                  type="button"
                  className="btn btn-outline-danger profile-btn"
                  onClick={() =>
                    download(
                      () => dataExchangeApi.importErrors(done.logId, "xlsx"),
                      "Could not download the failed rows.",
                    )
                  }
                >
                  <i className="bi bi-download"></i> Download failed rows
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary profile-btn"
                onClick={resetFile}
              >
                <i className="bi bi-arrow-repeat"></i> Import another file
              </button>
            </div>
            {(done.summary?.failed || 0) > 0 && (
              <p className="fs-075 text-slate-500 mt-2 mb-0">
                The failed-rows file has your original columns plus Row and
                Error. Fix them and upload it again.
              </p>
            )}
          </section>
        )}

        <p className="fs-080 text-slate-500 mt-4">
          <i className="bi bi-clock-history me-1"></i>
          Past imports and their failed rows are in{" "}
          <Link to="/importLogs">History</Link>.
        </p>
      </div>

      {saving && (
        <SaveTemplateDialog
          busy={createTemplate.isPending}
          onClose={() => setSaving(false)}
          onSave={({ name, description }) =>
            createTemplate.mutate(
              {
                dataset: datasetKey,
                kind: "IMPORT",
                name,
                description: description || undefined,
                columns: Object.entries(mapping)
                  .filter(([, f]) => f)
                  .map(([header, field]) => ({ field, header })),
                mode,
              },
              {
                onSuccess: (t) => {
                  toast.success("Mapping saved as a template.");
                  setTemplateId(t?.id || null);
                  setSaving(false);
                },
                onError: (err) =>
                  toast.error(
                    getApiErrorMessage(err, "Could not save the template."),
                  ),
              },
            )
          }
        />
      )}
    </Layout>
  );
};

export default ImportPage;
